/** Structured error facts returned by platform services. */
export interface ApiErrorFacts {
  code?: string;
  remediation?: string;
  /** The machine-readable cause of a refusal, such as `conversation_sealed` on a 409. */
  reason?: string;
}

/** HTTP failure with safe, user-facing structured platform error fields. */
export class ApiError extends Error {
  public readonly code?: string;
  public readonly remediation?: string;
  public readonly reason?: string;

  constructor(
    public readonly status: number,
    message: string,
    facts: ApiErrorFacts = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = facts.code;
    this.remediation = facts.remediation;
    this.reason = facts.reason;
  }
}

/** Read safe fields from either a top-level Nest error or its structured `error` envelope. */
export function apiErrorFromText(
  status: number,
  text: string,
  fallback: string,
): ApiError {
  const trimmed = text.trim();
  try {
    const parsed = JSON.parse(trimmed) as {
      code?: unknown;
      message?: unknown;
      remediation?: unknown;
      reason?: unknown;
      error?: unknown;
    };
    const nested =
      typeof parsed.error === 'object' && parsed.error !== null
        ? (parsed.error as {
            code?: unknown;
            message?: unknown;
            remediation?: unknown;
            reason?: unknown;
          })
        : parsed;
    const rawMessage = nested.message ?? parsed.message;
    const reason = nested.reason ?? parsed.reason;
    const message = Array.isArray(rawMessage)
      ? rawMessage
          .filter((value): value is string => typeof value === 'string')
          .join('; ')
      : typeof rawMessage === 'string' && rawMessage.trim()
        ? rawMessage.trim()
        : `${fallback} (${status})`;
    return new ApiError(status, message, {
      ...(typeof nested.code === 'string' && nested.code.trim()
        ? { code: nested.code.trim() }
        : {}),
      ...(typeof nested.remediation === 'string' && nested.remediation.trim()
        ? { remediation: nested.remediation.trim() }
        : {}),
      ...(typeof reason === 'string' && reason.trim()
        ? { reason: reason.trim() }
        : {}),
    });
  } catch {
    return new ApiError(
      status,
      trimmed && !trimmed.startsWith('{') ? trimmed : `${fallback} (${status})`,
    );
  }
}

/** Convert a failed response into a structured, display-safe error. */
export async function apiErrorFromResponse(
  response: Response,
  fallback: string,
): Promise<ApiError> {
  const text = await response.text().catch(() => '');
  return apiErrorFromText(response.status, text, fallback);
}

/** Message plus non-duplicated remediation for actionable UI feedback. */
export function apiErrorAction(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  if (
    error instanceof ApiError &&
    error.remediation &&
    error.remediation !== error.message
  ) {
    return `${error.message} ${error.remediation}`;
  }
  return error.message || fallback;
}
